import { HandInDateDialogRenderer } from "$/components/universal-job-card/hand-in-date-dialog-context"
import { useUpdateHandInDateMutation } from "$/lib/queries/job-queries"
import { Button } from "@repo/ui/components/ui/button"
import { DatePicker } from "@repo/ui/components/ui/date-picker"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@repo/ui/components/ui/dialog"
import { Label } from "@repo/ui/components/ui/typography"
import React from "react"
import { useTranslations } from "use-intl"

interface EditHandInDateDialogProps {
  jobId: string
  currentHandInDate: Date
  children?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function EditHandInDateDialog({
  jobId,
  currentHandInDate,
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: EditHandInDateDialogProps) {
  const t = useTranslations("alfred.jobs.editHandInDateDialog")
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen
  const setOpen = isControlled ? controlledOnOpenChange! : setUncontrolledOpen

  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
    new Date(currentHandInDate),
  )

  const updateHandInDateMutation = useUpdateHandInDateMutation(jobId, () => {
    setOpen(false)
  })

  React.useEffect(() => {
    if (open) {
      setSelectedDate(new Date(currentHandInDate))
    }
  }, [open, currentHandInDate])

  const handleSubmit = () => {
    if (selectedDate) {
      updateHandInDateMutation.mutate(selectedDate)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label size="s" weight="medium">
              {t("serviceDate")}
            </Label>
            <DatePicker
              value={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              placeholder={t("datePlaceholder")}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!selectedDate || updateHandInDateMutation.isPending}
            >
              {updateHandInDateMutation.isPending ? t("saving") : t("save")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Provided to UniversalJobCard via HandInDateDialogRendererProvider. */
export const renderEditHandInDateDialog: HandInDateDialogRenderer = ({
  jobId,
  currentHandInDate,
  onOpenChange,
}) => (
  <EditHandInDateDialog
    jobId={jobId}
    currentHandInDate={currentHandInDate}
    open={true}
    onOpenChange={onOpenChange}
  />
)
